/* ==========================================================================
   IntakeConfirmation Component
   Task F34: Success state after intake form submission
   Pattern based on CheckoutSuccess.tsx timeline
   ========================================================================== */

import { CheckCircle, ClipboardCheck, Settings, Mail, Rocket } from 'lucide-react'

const STEPS = [
  {
    icon: ClipboardCheck,
    title: "We've received your submission",
    description: 'Your intake form has been successfully submitted.',
    done: true,
  },
  {
    icon: Settings,
    title: 'Our team will review and set up your portal',
    description:
      "We'll configure your branding, formulary, and portal URL based on your preferences.",
    done: false,
  },
  {
    icon: Mail,
    title: "You'll receive an email when your portal is ready",
    description:
      "Once everything is set up, we'll send you an email with your admin login credentials.",
    done: false,
  },
  {
    icon: Rocket,
    title: 'After the go-live review, you get your admin login',
    description:
      'A quick call to make sure everything looks great, then you can start inviting providers.',
    done: false,
  },
]

export function IntakeConfirmation() {
  return (
    <div className="max-w-2xl mx-auto">
      {/* Success header */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6">
          <CheckCircle className="h-8 w-8 text-green-600" aria-hidden="true" />
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl text-charcoal mb-3">
          Intake form submitted!
        </h1>
        <p className="text-lg text-charcoal/70">
          Thank you for completing the intake form. Here's what happens next.
        </p>
      </div>

      {/* Timeline */}
      <div className="relative">
        {STEPS.map((step, index) => {
          const Icon = step.icon
          const isLast = index === STEPS.length - 1

          return (
            <div key={step.title} className="flex gap-4 mb-8 last:mb-0">
              <div className="flex flex-col items-center">
                <div
                  className={`flex items-center justify-center w-10 h-10 rounded-full flex-shrink-0 ${
                    step.done ? 'bg-green-100' : 'bg-soft-lavender'
                  }`}
                >
                  <Icon
                    className={`h-5 w-5 ${
                      step.done ? 'text-green-600' : 'text-deep-purple'
                    }`}
                    aria-hidden="true"
                  />
                </div>
                {!isLast && (
                  <div className="w-px flex-1 bg-charcoal/10 mt-2" />
                )}
              </div>
              <div className="pt-1 pb-4">
                <p className="text-xs font-semibold text-deep-purple uppercase tracking-wide mb-1">
                  Step {index + 1}
                </p>
                <h3 className="font-semibold text-charcoal mb-1">
                  {step.title}
                </h3>
                <p className="text-sm text-charcoal/70">{step.description}</p>
              </div>
            </div>
          )
        })}
      </div>

      {/* Contact info */}
      <div className="mt-12 text-center bg-white rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-6">
        <Mail
          className="h-6 w-6 text-deep-purple mx-auto mb-3"
          aria-hidden="true"
        />
        <p className="font-semibold text-charcoal mb-1">
          Have questions about your portal setup?
        </p>
        <p className="text-sm text-charcoal/60">
          Contact us at{' '}
          <a
            href="mailto:onboarding@tjmlabs.com"
            className="text-deep-purple hover:underline"
          >
            onboarding@tjmlabs.com
          </a>
        </p>
      </div>
    </div>
  )
}

export default IntakeConfirmation
